<template>
    <div class="home-content">
        <a id="profile-link" :href="adminUrl" target="_blank">
            <i class="icon-person"></i>
        </a>
        <LocaleSwitcher class="locale-switcher" />
        <div id="video-fullscreen-borders" :class="{ hidden: !fullscreenEnabled }" />
        <a
            :href="$t('home.adBannerUrl')"
            target="_blank"
            class="contact-banner"
            v-if="$t('home.adBannerTitle').length || $t('home.adBannerText').length"
        >
            <b>{{ $t("home.adBannerTitle") }} </b>
            <span>
                {{ $t("home.adBannerText") }}
            </span>
            <img :src="require('@/assets/gift@2x.png')" class="gift" />
        </a>
        <section id="middle-section" v-show="contentBanned || videoReady">
            <ContactCTAButtons />
            <transition name="fade" appear v-if="contentBanned">
                <div class="content-banned-message">
                    {{ $t("report.bannedVideoMessage") }}
                </div>
            </transition>
            <transition name="fade" appear v-else>
                <div
                    id="video-container"
                    :class="{ fullscreen: fullscreenEnabled }"
                    ref="videoContainer"
                >
                    <div id="videoFrame">
                        <video
                            playsinline
                            id="home-video"
                            :src="videoUrl"
                            @click="togglePlay"
                            preload="metadata"
                            ref="homeVideo"
                            :class="{ flipped: flipVideo }"
                            :poster="poster"
                        />
                        <DownloadButton
                            id="fullscreen-download-button"
                            v-if="fullscreenEnabled && displayDownloadButton"
                            :icon="true"
                            @click.prevent="downloadVideo"
                        />
                    </div>
                    <FullscreenButton
                        class="fullscreen-btn"
                        @click="toggleFullscreen"
                        v-if="displayFullscreenButtons"
                        :fullscreenEnabled="fullscreenEnabled"
                    />
                    <a
                        v-if="displayReportButton"
                        class="report-btn"
                        @click.prevent="() => this.$emit('report-btn-clicked')"
                    >
                        {{ $t("report.report") }}
                    </a>
                    <img
                        @click="togglePlay"
                        id="play-button"
                        :src="require('@/assets/play.png')"
                        v-if="!isPlayingVideo"
                    />
                </div>
            </transition>
            <LinkCTAButtons />
        </section>

        <Loader v-if="!contentBanned && (!videoReady || !videoUrl.length)" />

        <!-- Video Picker -->
        <div class="record-share-container" v-show="!fullscreenEnabled && videoReady">
            <transition name="cta-anim" appear>
                <div
                    v-if="displayTextResponseButton"
                    class="reply-video-button"
                    @click="openReplyVideo"
                >
                    {{ $t("reply.btnReply") }}
                </div>
            </transition>
            <transition name="cta-anim" appear>
                <label
                    v-if="(displayRecordButton || displayEditButton) && !displayTextResponseButton"
                    for="videoPick"
                    id="record-video-button"
                >
                    <div class="red-dot"></div>
                    {{ displayEditButton ? $t("home.edit") : $t("home.record") }}
                    <input id="videoPick" type="file" accept="video/*" @change="onVideoPick" />
                </label>
            </transition>
            <transition name="cta-anim" appear>
                <ShareButton v-if="displayShareButton" @click="share" />
            </transition>
            <transition name="cta-anim" appear>
                <DownloadButton v-if="displayDownloadButton" @click.prevent="downloadVideo" />
            </transition>
        </div>

        <RGPDLink />
        <GcuLink class="gcu-link" v-if="$t('general.GCU').length > 0" />
    </div>
</template>

<script lang="ts">
import GcuLink from "@/components/gcuLink.vue";
import LocaleSwitcher from "@/components/LocaleSwitcher.vue";
import Loader from "@/components/Loader.vue";
import ContactCTAButtons from "@/components/ContactCTAButtons.vue";
import LinkCTAButtons from "@/components/LinkCTAButtons.vue";
import FullscreenButton from "@/components/buttons/FullscreenButton.vue";
import ShareButton from "@/components/buttons/ShareButton.vue";
import RGPDLink from "@/components/RGPDLink.vue";
import DownloadButton from "@/components/buttons/DownloadButton.vue";
import { ErrorStatus } from "@/typings/Error";
import { setPoster } from "@/services/PosterGenerator";

export default {
    name: "HomeContentClassic",
    components: {
        Loader,
        GcuLink,
        LocaleSwitcher,
        FullscreenButton,
        ShareButton,
        ContactCTAButtons,
        LinkCTAButtons,
        RGPDLink,
        DownloadButton
    },
    emits: ["report-btn-clicked", "error"],
    props: {
        adminUrl: {
            type: String,
            default: ""
        },
        videoUrl: {
            type: String,
            default: ""
        },
        flipVideo: {
            type: Boolean,
            default: false
        },
        displayFullscreenButtons: {
            type: Boolean,
            default: true
        },
        displayRecordButton: Boolean,
        displayTextResponseButton: Boolean,
        displayEditButton: Boolean,
        displayShareButton: Boolean,
        displayDownloadButton: Boolean,
        displayReportButton: {
            type: Boolean,
            default: true
        },
        onVideoPick: {
            type: Function,
            required: true
        },
        openReplyVideo: {
            type: Function,
            required: false
        },
        share: {
            type: Function,
            required: true
        },
        downloadVideo: {
            type: Function,
            required: true
        },
        contentBanned: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            fullscreenEnabled: false,
            videoReady: false,
            isPlayingVideo: false,
            poster: ""
        };
    },
    async mounted() {
        const videoHome = this.$refs.homeVideo as HTMLVideoElement;
        if (videoHome) {
            const timeout = setTimeout(() => {
                if (!this.videoReady) {
                    this.$emit("error", ErrorStatus.Timeout);
                }
            }, 15000);

            videoHome.oncanplay = () => {
                this.videoReady = true;
                clearTimeout(timeout);
            };

            videoHome.onerror = () => {
                this.$emit("error", ErrorStatus.LoadError);
                clearTimeout(timeout);
            };

            videoHome.removeEventListener("play", this.onVideoPlay);
            videoHome.removeEventListener("pause", this.onVideoStop);

            videoHome.addEventListener("play", this.onVideoPlay);
            videoHome.addEventListener("pause", this.onVideoStop);
            this.poster = await setPoster(videoHome);
        }
    },
    methods: {
        toggleFullscreen() {
            if (this.fullscreenEnabled) {
                this.exitFullScreen();
            } else {
                this.goFullScreen();
            }
        },
        goFullScreen() {
            const videoContainer = this.$refs.videoContainer;
            const boundingBox = videoContainer.getBoundingClientRect();

            this.fullscreenEnabled = true;
            document.documentElement.style.setProperty(
                "--fullscreenTopOffset",
                `${boundingBox.y}px`
            );
        },
        exitFullScreen() {
            this.fullscreenEnabled = false;
        },
        onVideoPlay() {
            this.isPlayingVideo = true;
        },
        onVideoStop() {
            this.isPlayingVideo = false;
        },
        togglePlay() {
            if (this.isPlayingVideo) {
                this.$refs.homeVideo.pause();
            } else {
                this.$refs.homeVideo.play();
            }
        }
    }
};
</script>
<style scoped>
.home-content {
    display: flex;
    flex-direction: column;
    align-items: center;
}

#profile-link {
    position: absolute;
    top: 20px;
    left: 20px;
    height: 30px;
    width: 30px;
    border-radius: 50%;
    background-color: var(--white);
    text-decoration: none;
    box-shadow: 2px 3px 5px 0px rgb(150 150 150 / 50%);
}

#videoPick {
    display: none;
}

#profile-link i {
    font-size: 1.7em;
    font-weight: bold !important;
    color: var(--black);
}

.locale-switcher {
    position: absolute;
    top: 20px;
    right: 20px;
}

.contact-banner {
    width: calc(95% - 30px);
    padding: 10px 15px;
    margin-right: 5px;
    margin-bottom: 15px;
    position: relative;
    background: var(--white);
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-content: flex-start;
    text-align: left;
    text-decoration: none;
    color: var(--screenTextColor);
    border-radius: 5px;
}

.contact-banner > b {
    /* line-height: calc(2 * var(--vh)); */
    font-size: 0.8em;
}

.contact-banner > span {
    /* margin-top: 0.5vh;
    font-size: 1.3vh; */
    margin-top: 0.5em;
    font-size: 0.65em;
    max-width: 90%;
}

.gift {
    position: absolute;
    bottom: -30px;
    right: -30px;
}

#play-button {
    width: 80px;
    height: auto;
    position: absolute;
    left: calc(50% - 40px);
    top: calc(60% - 40px);
}

.ar-button-container {
    position: absolute;
    left: 50%;
    bottom: 10px;
    transform: translateX(-50%);
}

#middle-section {
    max-width: 95vw;
    display: grid;
    grid-template-columns: 15% 70% 15%;
}

#video-container {
    grid-column: 2;
    position: relative;
    max-width: 80vw;
    border: 10px solid var(--white);
    background: var(--white);
    border-radius: 20px;
    box-shadow: 2px 3px 12px 0px rgb(150 150 150 / 50%);
    z-index: 20;
}

#video-container.fullscreen {
    position: absolute;
    left: 0;
    top: 0;
    height: calc(100vh - var(--fullscreenTopOffset));
    max-width: 100vw;
    width: 100vw;
    border: 0;
    border-radius: 0;
    background: var(--black);
    box-shadow: none;
}

#videoFrame {
    position: relative;
    display: flex;
    flex-direction: column;
    margin: auto;
    border-radius: 10px;
    background-color: var(--black);
    justify-content: center;
    align-items: center;
}

#home-video {
    max-height: 60vh;
    height: calc(60 * var(--vh));
    width: auto;
    margin: auto;
    transition: all 0.2s ease-in;
    max-width: 100%;
    border-radius: 10px;
    background-color: var(--black);
    object-fit: cover;
}

#home-video.flipped {
    transform: scaleX(-1);
}

#video-container.fullscreen #home-video {
    height: calc(100 * var(--vh));
    max-height: 100vh;
    width: 100%;
    border-radius: 0;
}

#video-fullscreen-borders {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--black);
    opacity: 1;
    z-index: 15;
}

#video-fullscreen-borders.hidden {
    opacity: 0;
    display: block !important;
    pointer-events: none;
}

.fullscreen-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    color: var(--white);
}

.report-btn {
    position: absolute;
    top: 20px;
    left: 20px;
    color: var(--white);
    text-decoration: none;
    text-shadow: 2px 3px 5px rgb(0 0 0 / 75%);
}

.content-banned-message {
    color: var(--white);
    background: var(--black);
    border-radius: 15px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 10px;
}

.record-share-container {
    margin-top: 10px;
    margin-bottom: 10px;
    /* position: absolute;
    bottom: 7vh; */
    display: flex;
    flex-direction: row;
    justify-content: center;
    z-index: 21;
}

.share-button,
.download-button {
    margin-left: 5px;
    margin-right: 5px;
}

#fullscreen-download-button {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
}

#record-video-button {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    font-weight: 500;
    background-color: var(--secondary);
    color: var(--buttonTextColor);
    font-size: 2.5vh;
    border-radius: calc(var(--vh) * 7);
    line-height: calc(var(--vh) * 6);
    height: calc(var(--vh) * 6);
    padding: 0 15px;
    margin-right: 5px;
}

.red-dot {
    height: 2.5vh;
    width: 2.5vh;
    background: var(--red);
    border-radius: 50%;
    margin-right: 10px;
}
</style>
